import { useCallback } from "react";
import { useAppSelector } from "@/shared/store/hooks";
import { selectCurrentUser } from "@/features/auth";

/**
 * Hook para verificar permissões do usuário logado.
 *
 * Usa os dados de `modulos` retornados pelo `/me` (cached no Redux).
 *
 * @example
 * const { hasPermission } = usePermission();
 * hasPermission("Usuários", "CRIAR") // true se o perfil tem a ação CRIAR no módulo Usuários
 */
export function usePermission() {
  const user = useAppSelector(selectCurrentUser);

  const modulos = user?.modulos;

  const hasPermission = useCallback(
    (modulo: string, acao: string): boolean => {
      if (!modulos) return false;
      return modulos.some(
        (m) => m.nome === modulo && m.acoes.includes(acao),
      );
    },
    [modulos],
  );

  const hasAnyPermission = useCallback(
    (modulo: string): boolean => {
      if (!modulos) return false;
      return modulos.some(
        (m) => m.nome === modulo && m.acoes.length > 0,
      );
    },
    [modulos],
  );

  /**
   * Retorna true se o usuário tem **qualquer uma** das combinações módulo/ação informadas.
   * Útil para telas com múltiplos pontos de entrada (ex.: Movimentações pode ser acessada
   * por quem tem VER em Movimentações OU CRIAR em Ajuste/Inventário).
   */
  const hasAnyOf = useCallback(
    (perms: ReadonlyArray<{ modulo: string; acao?: string }>): boolean => {
      if (!modulos) return false;
      return perms.some(({ modulo, acao }) =>
        modulos.some(
          (m) =>
            m.nome === modulo &&
            (acao ? m.acoes.includes(acao) : m.acoes.length > 0),
        ),
      );
    },
    [modulos],
  );

  return { hasPermission, hasAnyPermission, hasAnyOf };
}
